import { useState, useEffect } from 'react';
import { addMinutes, format, isBefore, isToday, startOfDay, endOfDay, startOfToday } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { 
  supabase, 
  Event, 
  EventInsert,
  getEvents
} from '@/integrations/supabase/client';
import { 
  temporaryCreateEvent,
  temporaryUpdateEvent,
  temporaryDeleteEvent
} from '@/utils/supabase-helpers';
import { logEventCreation, logEventDeletion } from '@/utils/action-logger';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { CalendarDays, Clock, Trash, Plus, Edit } from 'lucide-react';

export default function EventPlanner() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventType, setEventType] = useState('Black Belt Lesson');
  const [customType, setCustomType] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('09:30');
  const [comments, setComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Add useEffect to load events when component mounts
  useEffect(() => {
    if (selectedDate) {
      handleDateSelect(selectedDate);
    }
  }, []); // Empty dependency array means this runs once on mount
  
  const resetForm = () => {
    setSelectedEvent(null);
    setEventType('Black Belt Lesson');
    setCustomType('');
    setStartTime('09:00');
    setEndTime('09:30');
    setComments('');
  };

  const handleDateSelect = async (date: Date | undefined) => {
    if (!date) return;
    
    // Don't allow selecting past dates
    if (isBefore(date, startOfToday())) {
      toast({
        variant: "destructive",
        title: "Invalid Date",
        description: "Cannot select dates in the past"
      });
      return;
    }
    
    setSelectedDate(date);
    try {
      const startOfDayDate = startOfDay(date);
      const endOfDayDate = endOfDay(date);

      console.log('Fetching events for range:', startOfDayDate, 'to', endOfDayDate);
      const { data, error } = await getEvents(startOfDayDate, endOfDayDate);

      if (error) {
        console.error('Error fetching events:', error);
        throw error;
      }
      
      console.log('Events fetched:', data);
      if (Array.isArray(data)) {
        setEvents(data);
      } else {
        setEvents([]);
        console.error('Unexpected data format:', data);
      }
    } catch (error: any) {
      console.error('Error fetching events:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to load events for the selected date"
      });
    }
  };

  const openNewEventDialog = () => {
    // Check if selected date is in the past
    if (selectedDate && isBefore(selectedDate, startOfToday())) {
      toast({
        variant: "destructive",
        title: "Invalid Date",
        description: "Cannot create events in the past"
      });
      return;
    }
    resetForm();
    // For today, default start must be after "now" (resetForm alone would use 09:00).
    if (selectedDate && isToday(selectedDate)) {
      const startAnchor = addMinutes(new Date(), 10);
      setStartTime(format(startAnchor, 'HH:mm'));
      setEndTime(format(addMinutes(startAnchor, 30), 'HH:mm'));
    }
    setIsDialogOpen(true);
  };

  const openEditEventDialog = (event: Event) => {
    // Check if event is in the past
    const eventDate = new Date(event.start_time);
    if (isBefore(eventDate, startOfToday())) {
      toast({
        variant: "destructive",
        title: "Invalid Date",
        description: "Cannot edit events in the past"
      });
      return;
    }
    setSelectedEvent(event);
    setEventType(event.event_type);
    if (event.event_type === 'Other' && event.custom_type) {
      setCustomType(event.custom_type);
    }
    setStartTime(format(new Date(event.start_time), 'HH:mm'));
    setEndTime(format(new Date(event.end_time), 'HH:mm'));
    setComments(event.comments || '');
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (event: Event) => {
    // Check if event is in the past
    const eventDate = new Date(event.start_time);
    if (isBefore(eventDate, startOfToday())) {
      toast({
        variant: "destructive",
        title: "Invalid Date",
        description: "Cannot delete events in the past"
      });
      return;
    }
    setSelectedEvent(event);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!selectedDate || !user) return;
    
    try {
      setIsLoading(true);

      // For "Other" type, validate custom type
      if (eventType === 'Other' && !customType) {
        toast({
          variant: "destructive", 
          title: "Error", 
          description: "Please enter a custom event type"
        });
        return;
      }

      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      const startDateTime = new Date(`${formattedDate}T${startTime}`);
      const endDateTime = new Date(`${formattedDate}T${endTime}`);

      const now = new Date();
      if (isToday(selectedDate) && startDateTime < now) {
        toast({
          variant: "destructive",
          title: "Invalid start time",
          description:
            "For the current day, the start time cannot be before the current time.",
        });
        return;
      }
      
      if (startDateTime >= endDateTime) {
        toast({
          variant: "destructive",
          title: "Invalid times",
          description:
            "End time must be later than start time. Please correct the start and end times.",
        });
        return;
      }

      // Use event type as title, or custom type if "Other" is selected
      const title = eventType === 'Other' ? customType : eventType;

      const eventData: EventInsert = {
        title,
        event_type: eventType,
        custom_type: eventType === 'Other' ? customType : null,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        comments: comments || null,
        created_by: user.id
      };

      let response;
      
      if (selectedEvent) {
        // Update existing event - using temporary direct table access
        response = await temporaryUpdateEvent(selectedEvent.id, eventData);
      } else {
        // Insert new event - using temporary direct table access
        response = await temporaryCreateEvent(eventData);
        
        // Log event creation
        if (response.data && !response.error) {
          const eventId = response.data.id || response.data[0]?.id;
          if (eventId) {
            await logEventCreation(user.id, eventId);
          }
        }
      }

      if (response.error) throw response.error;
      
      setIsDialogOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: selectedEvent 
          ? "Event updated successfully" 
          : "Event created successfully"
      });
      
      // Refresh events for the selected date
      handleDateSelect(selectedDate);
    } catch (error: any) {
      console.error('Error saving event:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save event"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || !user) return;
    
    try {
      setIsLoading(true);
      
      // Using temporary direct table access
      const { error } = await temporaryDeleteEvent(selectedEvent.id);
        
      if (error) throw error;

      // Log event deletion
      await logEventDeletion(user.id, selectedEvent.id);
      
      setIsDeleteDialogOpen(false);
      setSelectedEvent(null);
      toast({
        title: "Success",
        description: "Event deleted successfully"
      });
      
      // Refresh events for the selected date
      handleDateSelect(selectedDate);
    } catch (error: any) {
      console.error('Error deleting event:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete event"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatEventTime = (event: Event) => {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
  };

  /** Parse HH:mm as a time on an arbitrary day (for duration math only). */
  const parseTimeHm = (hm: string) => new Date(`1970-01-01T${hm}`);

  /** When start time changes, set end to 30 minutes later. End changes never move start. */
  const handleStartTimeChange = (newStartTime: string) => {
    setStartTime(newStartTime);
    const endFromStart = addMinutes(parseTimeHm(newStartTime), 30);
    setEndTime(format(endFromStart, 'HH:mm'));
  };

  const handleEndTimeChange = (newEndTime: string) => {
    setEndTime(newEndTime);
  };

  
  return (
    <div className="w-full px-4">
      <h2 className="text-2xl font-semibold mb-6 mt-6 text-taekwondo-red">
        Event Planner
      </h2>
      <div className="flex flex-col md:flex-row gap-6">
        {/* Calendar Section */}
        <div className="w-full md:w-1/2">
          <div className="bg-gray-50/80 rounded-lg shadow-md p-4">
            <div className="flex justify-center">
              <Calendar 
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) => isBefore(date, startOfToday())}
                className="rounded-md border shadow bg-white"
              />
            </div>
          </div>
        </div>

        {/* Events List Section - Shows on right for desktop, below calendar for mobile */}
        <div className="w-full md:w-1/2">
          {selectedDate && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h3 className="text-lg font-medium">
                  Events for {format(selectedDate, 'MMMM d, yyyy')}
                </h3>
                <Button
                  variant="default"
                  size="sm"
                  onClick={openNewEventDialog}
                  className="self-end sm:self-auto flex items-center gap-1 bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Event</span>
                </Button>
              </div>
              
              {events.length > 0 ? (
                <div className="space-y-3">
                  {events.map(event => (
                    <div key={event.id} className="bg-gray-50 rounded-md p-4 border">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-lg text-taekwondo-red">{event.title}</h4>
                          <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <Clock className="h-4 w-4" />
                            <span>{formatEventTime(event)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => openEditEventDialog(event)}
                            className="text-taekwondo-red hover:text-taekwondo-red/80 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => openDeleteDialog(event)}
                            className="text-taekwondo-red hover:text-taekwondo-red/80 transition-colors"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {event.comments && (
                        <div className="mt-3 text-sm text-gray-600 bg-gray-100 p-3 rounded">
                          {event.comments}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                  <CalendarDays className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 font-medium">No events scheduled</p>
                  <p className="text-sm text-gray-500 mt-1">Click 'Add Event' to create a new event</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Event Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{selectedEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
            <DialogDescription>
              {selectedEvent ? 'Edit the event details below.' : 'Add a new event to the calendar.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="event-type">Event Type</Label>
              <Select
                value={eventType}
                onValueChange={setEventType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Black Belt Lesson">Black Belt Lesson</SelectItem>
                  <SelectItem value="Grading">Grading</SelectItem>
                  <SelectItem value="Competition">Competition</SelectItem>
                  <SelectItem value="Seminar">Seminar</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {eventType === 'Other' && (
              <div className="grid gap-2">
                <Label htmlFor="custom-type">Custom Event Type</Label>
                <Input
                  id="custom-type"
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  placeholder="Enter custom event type"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="start-time">Start Time</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="end-time">End Time</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="comments">Comments</Label>
              <Textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any additional information"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)} variant="outline">Cancel</Button>
            <Button onClick={handleSaveEvent} disabled={isLoading}>
              {isLoading ? 'Saving...' : (selectedEvent ? 'Update Event' : 'Create Event')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the event
              from the calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isLoading ? 'Deleting...' : 'Delete Event'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
